import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';
import { z } from 'zod';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import type { DeepUnknown } from '@/types/deep-unknown';

import { TaskSchema } from '../entities/task';
import { WorkstreamSchema } from '../entities/workstream';

export namespace WorkstreamsQuery {
  export const key = (input: Input) => [
    'project-management',
    'workstreams',
    ...(input.siteId ? [input.siteId] : []),
  ];

  export type Input = { siteId?: string };

  export const OutputSchema = z.object({
    workstreams: z.array(
      WorkstreamSchema.pick({
        id: true,
        name: true,
        description: true,
        progress: true,
        startDate: true,
        endDate: true,
      }).extend({
        tasks: z.array(
          TaskSchema.pick({
            id: true,
            title: true,
            description: true,
            dueDate: true,
            priority: true,
            status: true,
            assignee: true,
          }),
        ),
      }),
    ),
  });
  export type Output = z.infer<typeof OutputSchema>;

  function outputMapper(
    data: FeApi.ListWorkstreamsViewOutput[],
  ): DeepUnknown<Output> {
    return {
      workstreams: data.map((ws) => ({
        id: ws.uuid,
        startDate: new Date(ws.start_date),
        endDate: new Date(ws.end_date),
        name: ws.name,
        description: ws.description,
        progress: ws.progress / 100,
        tasks:
          ws.cards.map((task) => ({
            id: task.uuid,
            title: task.title,
            description: task.description,
            dueDate: task.due_date ? new Date(task.due_date) : undefined,
            status: task.status,
            priority: task.priority,
            assignee: task.assignee || undefined,
          })) ?? [],
      })),
    };
  }

  function useQueryFn() {
    const workstreamsList = useInjectFeApiContext(
      FeApi.tasksV1WorkstreamsListList,
    );

    return async function queryFn(input: Input) {
      const workstreamsResult = await ok(
        workstreamsList({
          siteAccureId: input.siteId,
        }),
      );

      const output = await OutputSchema.parseAsync(
        outputMapper(workstreamsResult),
      );

      return output;
    };
  }

  export function useQuery(input: Input) {
    const queryFn = useQueryFn();

    return rq.useQuery<Output>({
      queryKey: key(input),
      queryFn: () => queryFn(input),
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();

    return async function prefetch(input: Input): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(input),
        queryFn: () => queryFn(input),
      });
    };
  }
}
